/** Domain vocabulary shared by the API, the agent app and the widget. */

export const ROLES = ['owner', 'admin', 'agent'] as const;
export type Role = (typeof ROLES)[number];

/** Roles allowed into the Management area (WP: `USER_ROLE == 'owner' or 'admin'`). */
export const MANAGER_ROLES: readonly Role[] = ['owner', 'admin'];

/** Channel a conversation arrived on. `fmp` = Facebook Messenger Platform. */
export const SESSION_TYPES = ['chat', 'sms', 'email', 'fmp', 'craigslist', 'form'] as const;
export type SessionType = (typeof SESSION_TYPES)[number];

/**
 * How a message was delivered. The original mutated this column after a send —
 * a widget message relayed over SMS was rewritten from `message` to `sms` — so
 * the value doubles as the delivery receipt channel.
 */
export const MESSAGE_TYPES = ['message', 'images', 'sms', 'email'] as const;
export type MessageType = (typeof MESSAGE_TYPES)[number];

export const MESSAGE_SOURCES = [
  'widget',
  'agent',
  'craigslist',
  'form',
  'auto-response',
  'system',
] as const;
export type MessageSource = (typeof MESSAGE_SOURCES)[number];

export const TEMPLATE_TYPES = ['welcome', 'no_agents_online', 'inventory_notifications'] as const;
export type TemplateType = (typeof TEMPLATE_TYPES)[number];

export const TEMPLATE_LABELS: Record<TemplateType, string> = {
  welcome: 'Welcome',
  no_agents_online: 'No Agents Online',
  inventory_notifications: 'Inventory Notifications',
};

export const AUTO_RESPONSE_SOURCES = ['any', 'chat', 'sms', 'email', 'fmp'] as const;
export type AutoResponseSource = (typeof AUTO_RESPONSE_SOURCES)[number];

export const AUTO_RESPONSE_CONDITIONS = [
  'message',
  'customer_name',
  'customer_email',
  'customer_phone',
] as const;
export type AutoResponseCondition = (typeof AUTO_RESPONSE_CONDITIONS)[number];

export const AUTO_RESPONSE_EQUATIONS = [
  'equal',
  'not_equal',
  'contains',
  'not_contains',
  'starts',
  'ends',
] as const;
export type AutoResponseEquation = (typeof AUTO_RESPONSE_EQUATIONS)[number];

export const BLACKLIST_KEYS = ['email', 'phone'] as const;
export type BlacklistKey = (typeof BLACKLIST_KEYS)[number];

/** Sidebar buckets in the agent app: unassigned vs. mine. */
export const SESSION_CATEGORIES = ['new', 'my'] as const;
export type SessionCategory = (typeof SESSION_CATEGORIES)[number];

/** Reply-to mailbox pattern that routes an email answer back to its session. */
export const SESSION_EMAIL_PREFIX = 'session-';

export const DEFAULT_TEMPLATES: Record<TemplateType, string> = {
  welcome: 'Hi! Thanks for reaching out. How can we help you today?',
  no_agents_online:
    "Thanks for your message! Nobody is available right now, but we've emailed the team and someone will reply shortly.",
  // {{item}} is the item's title; any field key the workspace defined also works
  // as a placeholder, e.g. {{make}} once a field called "make" exists.
  inventory_notifications:
    'Good news — we just got {{item}} in stock. Reply here if you would like details.',
};

// ---------------------------------------------------------------------------
// Inventory
// ---------------------------------------------------------------------------

/**
 * The kinds a workspace owner can give a field.
 *
 * Deliberately short. Every kind costs a validator, an input control, a filter
 * control and a sort rule, and a list that grows without limit is how the old
 * "custom fields" screens ended up unusable.
 */
export const FIELD_KINDS = ['text', 'number', 'select', 'date'] as const;
export type FieldKind = (typeof FIELD_KINDS)[number];

export const FIELD_KIND_LABELS: Record<FieldKind, string> = {
  text: 'Text',
  number: 'Number',
  select: 'Choice',
  date: 'Date',
};

export const FIELD_KIND_HINTS: Record<FieldKind, string> = {
  text: 'Any short text — a name, a code, a link, several values separated by commas.',
  number: 'Digits only. Compared as a number rather than as text.',
  select: 'One value from a list you define.',
  date: 'A calendar date.',
};

/**
 * How wide a field sits on the add/edit form, as a percentage of the row.
 *
 * Offered as presets rather than a free number because the form is a grid: a
 * value that does not divide the row leaves a ragged gap, and three fields at
 * 33% sitting in one line is the entire point of the setting.
 */
export const FIELD_WIDTHS = [100, 50, 33, 25] as const;
export type FieldWidth = (typeof FIELD_WIDTHS)[number];

export const FIELD_WIDTH_LABELS: Record<number, string> = {
  100: '100% — its own row',
  50: '50% — two per row',
  33: '33% — three per row',
  25: '25% — four per row',
};

/** The form is a twelve-column grid, so a percentage has to become columns. */
export function widthToColumns(percent: number): number {
  return Math.min(12, Math.max(1, Math.round((percent / 100) * 12)));
}

/** A field a customer can be alerted on has to be filterable. */
export const RULE_OPS = ['equals', 'contains'] as const;
export type RuleOp = (typeof RULE_OPS)[number];

/**
 * Ceilings. Not arbitrary: every filterable field is an extra row per item, and
 * the field editor stops being readable long before 30 rows.
 */
export const MAX_FIELDS_PER_WORKSPACE = 30;
export const MAX_FIELD_OPTIONS = 100;
export const MAX_ITEM_IMAGES = 12;
/** One import call. Above this the caller should page. */
export const MAX_ITEMS_PER_IMPORT = 500;
/**
 * Above this many new items in one call, matching rules are left to the hourly
 * sweep instead of running inline — a rule with no criteria matches everything,
 * so an inline pass would turn one import into thousands of messages.
 */
export const INLINE_NOTIFY_LIMIT = 50;

/**
 * Keys per workspace. Enough for a feed, a stock-taking script and a spare to
 * rotate through, and few enough that the list stays a list.
 */
export const MAX_API_KEYS_PER_WORKSPACE = 10;

/** Every key starts with this, so one is recognisable in a log or a paste. */
export const API_KEY_PREFIX = 'lgk_';

/** Reserved because they collide with the item's own columns. */
export const RESERVED_FIELD_KEYS = [
  'id',
  'uuid',
  'title',
  'data',
  'images',
  'externalId',
  'external_id',
  'createdAt',
  'updatedAt',
  'deletedAt',
  'batchId',
  'item',
] as const;
